import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtMoney, API } from "@/lib/api";
import { Download, Printer } from "lucide-react";

export default function StubDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    (async () => {
      const [stub, c] = await Promise.all([api.get(`/paystubs/${id}`), api.get("/company")]);
      setS(stub.data); setCompany(c.data);
    })();
  }, [id]);

  if (!s) return <div className="p-10 font-mono">Loading…</div>;
  const emp = s.employee_snapshot;

  return (
    <div className="p-10">
      <div className="flex items-center justify-between mb-6">
        <Link to="/app/paystubs" className="overline">← Back to Pay Stubs</Link>
        <div className="flex gap-2">
          <button onClick={() => window.print()} className="border-2 border-ink px-4 py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2"><Printer className="w-4 h-4" /> Print</button>
          <a data-testid="download-pdf" href={`${API}/paystubs/${id}/pdf`} target="_blank" rel="noreferrer" className="hard-btn px-4 py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2"><Download className="w-4 h-4" /> Download PDF</a>
        </div>
      </div>

      <div className="max-w-3xl mx-auto bg-white border-2 border-ink shadow-hard-lg p-8">
        {/* Check portion */}
        <div className="border-2 border-ink p-6">
          <div className="flex justify-between items-start mb-6 pb-4 border-b-2 border-ink">
            <div>
              <div className="font-heading font-black text-lg">{company?.name?.toUpperCase()}</div>
              <div className="text-xs font-mono text-muted-foreground">{company?.address}</div>
              <div className="text-xs font-mono text-muted-foreground">{company?.city} {company?.state} {company?.zip_code}</div>
            </div>
            <div className="text-right">
              <div className="overline text-[10px]">CHECK NO.</div>
              <div className="font-heading font-black text-2xl">{String(s.check_number).padStart(4, "0")}</div>
              <div className="overline text-[10px] mt-2">DATE</div>
              <div className="font-mono text-sm">{s.pay_date}</div>
            </div>
          </div>
          <div className="mb-4">
            <div className="overline text-[10px] mb-1">PAY TO THE ORDER OF</div>
            <div className="font-heading font-black text-xl uppercase border-b-2 border-ink pb-1">{emp.first_name} {emp.last_name}</div>
          </div>
          <div className="flex items-center justify-between mb-6">
            <div className="font-mono italic text-sm">
              {Math.floor(s.net_pay)} dollars and {String(Math.round((s.net_pay - Math.floor(s.net_pay)) * 100)).padStart(2, "0")}/100
            </div>
            <div className="border-2 border-ink px-4 py-2 font-mono font-black text-xl">{fmtMoney(s.net_pay)}</div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="font-mono text-xs">{company?.bank_name}</div>
              <div className="font-mono text-[10px] text-muted-foreground mt-1">MEMO: Payroll {s.period_start} — {s.period_end}</div>
            </div>
            <div className="text-right">
              <div className="border-b-2 border-ink pb-1 mb-1">&nbsp;</div>
              <div className="overline text-[10px]">AUTHORIZED SIGNATURE</div>
            </div>
          </div>
          <div className="mt-6 font-mono text-sm tracking-widest">
            ⑈{String(s.check_number).padStart(4, "0")}⑈  ⑆{company?.bank_routing || "000000000"}⑆  {company?.bank_account || "0000000000"}⑈
          </div>
        </div>

        {/* Stub portion */}
        <div className="mt-6 border-t-2 border-dashed border-ink pt-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="font-heading font-black text-xl">EARNINGS STATEMENT</div>
              <div className="overline">Pay Period {s.period_start} → {s.period_end}</div>
            </div>
            <div className="text-right font-mono text-xs">
              <div><b>Pay Date:</b> {s.pay_date}</div>
              <div><b>Check #:</b> {s.check_number}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 mb-6 font-mono text-xs">
            <div className="border-2 border-ink p-3">
              <div className="overline mb-2">EMPLOYEE</div>
              <div>{emp.first_name} {emp.last_name}</div>
              <div className="text-muted-foreground">{emp.address}</div>
              <div className="text-muted-foreground">{emp.city} {emp.state} {emp.zip_code}</div>
            </div>
            <div className="border-2 border-ink p-3">
              <div className="overline mb-2">COMPANY</div>
              <div>{company?.name}</div>
              <div className="text-muted-foreground">EIN: {company?.ein}</div>
              <div className="text-muted-foreground">{company?.city} {company?.state}</div>
            </div>
          </div>

          <table className="w-full font-mono text-sm mb-4">
            <thead className="bg-ink text-white">
              <tr>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-widest">Earnings</th>
                <th className="text-right px-3 py-2 text-xs uppercase tracking-widest">Hours</th>
                <th className="text-right px-3 py-2 text-xs uppercase tracking-widest">Current</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className="border-t border-ink/40 px-3 py-1.5">Regular</td><td className="text-right border-t border-ink/40 px-3 py-1.5">{s.hours_worked?.toFixed(2)}</td><td className="text-right border-t border-ink/40 px-3 py-1.5">{fmtMoney(s.regular_pay)}</td></tr>
              {s.overtime_hours > 0 && <tr><td className="border-t border-ink/40 px-3 py-1.5">Overtime (1.5×)</td><td className="text-right border-t border-ink/40 px-3 py-1.5">{s.overtime_hours?.toFixed(2)}</td><td className="text-right border-t border-ink/40 px-3 py-1.5">{fmtMoney(s.overtime_pay)}</td></tr>}
              {s.bonus > 0 && <tr><td className="border-t border-ink/40 px-3 py-1.5">Bonus</td><td className="text-right border-t border-ink/40 px-3 py-1.5">—</td><td className="text-right border-t border-ink/40 px-3 py-1.5">{fmtMoney(s.bonus)}</td></tr>}
              <tr className="font-black bg-secondary"><td className="px-3 py-2 border-t-2 border-ink">GROSS PAY</td><td /><td className="text-right px-3 py-2 border-t-2 border-ink">{fmtMoney(s.gross)}</td></tr>
            </tbody>
          </table>

          <table className="w-full font-mono text-sm">
            <thead className="bg-ink text-white">
              <tr>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-widest">Deductions</th>
                <th className="text-right px-3 py-2 text-xs uppercase tracking-widest">Current</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className="border-t border-ink/40 px-3 py-1.5">Federal Income Tax</td><td className="text-right border-t border-ink/40 px-3 py-1.5 text-bad">{fmtMoney(s.federal_tax)}</td></tr>
              <tr><td className="border-t border-ink/40 px-3 py-1.5">Social Security (6.2%)</td><td className="text-right border-t border-ink/40 px-3 py-1.5 text-bad">{fmtMoney(s.social_security)}</td></tr>
              <tr><td className="border-t border-ink/40 px-3 py-1.5">Medicare (1.45%)</td><td className="text-right border-t border-ink/40 px-3 py-1.5 text-bad">{fmtMoney(s.medicare)}</td></tr>
              <tr><td className="border-t border-ink/40 px-3 py-1.5">State Tax ({emp.state})</td><td className="text-right border-t border-ink/40 px-3 py-1.5 text-bad">{fmtMoney(s.state_tax)}</td></tr>
              {s.sui_tax > 0 && <tr data-testid="stub-sui-row"><td className="border-t border-ink/40 px-3 py-1.5">State Unemployment (SUI · {emp.state})</td><td className="text-right border-t border-ink/40 px-3 py-1.5 text-bad">{fmtMoney(s.sui_tax)}</td></tr>}
              {s.sdi_tax > 0 && <tr data-testid="stub-sdi-row"><td className="border-t border-ink/40 px-3 py-1.5">State Disability (SDI · {emp.state})</td><td className="text-right border-t border-ink/40 px-3 py-1.5 text-bad">{fmtMoney(s.sdi_tax)}</td></tr>}
              {s.pfl_tax > 0 && <tr data-testid="stub-pfl-row"><td className="border-t border-ink/40 px-3 py-1.5">{s.pfl_label || "Paid Family Leave"}</td><td className="text-right border-t border-ink/40 px-3 py-1.5 text-bad">{fmtMoney(s.pfl_tax)}</td></tr>}
              {s.ltc_tax > 0 && <tr data-testid="stub-ltc-row"><td className="border-t border-ink/40 px-3 py-1.5">{s.ltc_label || "Long-Term Care"}</td><td className="text-right border-t border-ink/40 px-3 py-1.5 text-bad">{fmtMoney(s.ltc_tax)}</td></tr>}
              {s.local_tax > 0 && <tr data-testid="stub-local-row"><td className="border-t border-ink/40 px-3 py-1.5">Local Income Tax{s.local_jurisdiction ? ` (${s.local_jurisdiction.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase())})` : ""}</td><td className="text-right border-t border-ink/40 px-3 py-1.5 text-bad">{fmtMoney(s.local_tax)}</td></tr>}
              {s.k401_deduction > 0 && <tr data-testid="stub-401k-row"><td className="border-t border-ink/40 px-3 py-1.5">401(k) — pre-tax</td><td className="text-right border-t border-ink/40 px-3 py-1.5 text-bad">{fmtMoney(s.k401_deduction)}</td></tr>}
              {s.section125_deduction > 0 && <tr data-testid="stub-s125-row"><td className="border-t border-ink/40 px-3 py-1.5">Section 125 — pre-tax</td><td className="text-right border-t border-ink/40 px-3 py-1.5 text-bad">{fmtMoney(s.section125_deduction)}</td></tr>}
              {s.other_deductions > 0 && <tr><td className="border-t border-ink/40 px-3 py-1.5">Other</td><td className="text-right border-t border-ink/40 px-3 py-1.5 text-bad">{fmtMoney(s.other_deductions)}</td></tr>}
              <tr className="font-black bg-secondary"><td className="px-3 py-2 border-t-2 border-ink">NET PAY</td><td className="text-right px-3 py-2 border-t-2 border-ink text-good">{fmtMoney(s.net_pay)}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
